/*
 * LawFirmMarketingRoi
 * Uses only the numbers the reader enters, for the same period (for example one month). No defaults, no benchmarks.
 *
 * Inputs (required): marketing spend ($, fees plus ad spend), signed cases from marketing, average fee per signed case ($)
 * Input (optional): leads from marketing
 *
 * Rules
 *   cost per signed case = spend / cases
 *   fees                 = cases * average fee
 *   return per $1        = fees / spend
 *   ROI %                = (fees - spend) / spend * 100
 *   if leads entered (and leads >= cases): cost per lead = spend / leads; lead-to-case rate = cases / leads * 100
 *   if leads entered but fewer than cases: show a message instead of the lead figures
 * Results show only when the three required inputs are valid numbers greater than 0.
 * Fees are what the firm actually expects to collect; contingency fees may arrive months later.
 * No tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";

type Key = "spend" | "cases" | "fee" | "leads";
type Field = { key: Key; label: string; hint: string; placeholder: string; optional?: boolean };

const FIELDS: Field[] = [
  { key: "spend", label: "Marketing spend ($)", hint: "Agency fees plus ad spend for the period", placeholder: "e.g. 6000" },
  { key: "cases", label: "Signed cases from marketing", hint: "Same period", placeholder: "e.g. 4" },
  { key: "fee", label: "Average fee per signed case ($)", hint: "What you expect to collect per case", placeholder: "e.g. 7500" },
  { key: "leads", label: "Leads from marketing (optional)", hint: "Calls, forms and chats in the same period", placeholder: "e.g. 60", optional: true },
];

const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n < 100 ? 2 : 0 });
const one = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });

function parse(raw: string): { value: number | null; error: string | null } {
  const t = raw.replace(/[$,\s]/g, "");
  if (t === "") return { value: null, error: null };
  const n = Number(t);
  if (!Number.isFinite(n)) return { value: null, error: "Enter a number." };
  if (n <= 0) return { value: null, error: "Enter a number greater than 0." };
  return { value: n, error: null };
}

export default function LawFirmMarketingRoi({ ctaHref = "#free-plan", ctaText = "Get a free tracking audit →" }: { ctaHref?: string; ctaText?: string }) {
  const uid = useId();
  const [v, setV] = useState<Record<Key, string>>({ spend: "", cases: "", fee: "", leads: "" });
  const p = Object.fromEntries(FIELDS.map((f) => [f.key, parse(v[f.key])])) as Record<Key, { value: number | null; error: string | null }>;

  const spend = p.spend.value;
  const cases = p.cases.value;
  const fee = p.fee.value;
  const leads = p.leads.value;
  const ready = spend !== null && cases !== null && fee !== null;

  const out = ready
    ? (() => {
        const fees = (cases as number) * (fee as number);
        return {
          cpc: (spend as number) / (cases as number),
          fees,
          perDollar: fees / (spend as number),
          roi: ((fees - (spend as number)) / (spend as number)) * 100,
        };
      })()
    : null;

  const leadInfo =
    ready && leads !== null
      ? leads >= (cases as number)
        ? { ok: true as const, cpl: (spend as number) / leads, rate: ((cases as number) / leads) * 100 }
        : { ok: false as const }
      : null;

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Law firm marketing ROI calculator</h3>
      <p className="mt-1 text-sm text-muted-foreground">Use numbers from the same period. Nothing is saved or sent anywhere.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => {
          const id = `${uid}-${f.key}`;
          const err = p[f.key].error;
          return (
            <div key={f.key}>
              <label htmlFor={id} className="block text-sm font-medium">{f.label}</label>
              <input
                id={id}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder={f.placeholder}
                value={v[f.key]}
                onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))}
                aria-invalid={err ? true : undefined}
                aria-describedby={`${id}-hint`}
                className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
              />
              <p id={`${id}-hint`} className="mt-1 text-xs text-muted-foreground">{err ?? f.hint}</p>
            </div>
          );
        })}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!out ? (
          <p className="text-muted-foreground">Enter spend, signed cases and average fee to see your results.</p>
        ) : (
          <div>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div className="border-b border-border py-3"><dt className="text-muted-foreground">Cost per signed case</dt><dd className="text-base font-semibold">{usd(out.cpc)}</dd></div>
              <div className="border-b border-border py-3"><dt className="text-muted-foreground">Fees from these cases</dt><dd className="text-base font-semibold">{usd(out.fees)}</dd></div>
              <div className="border-b border-border py-3"><dt className="text-muted-foreground">Return for every $1 spent</dt><dd className="text-base font-semibold">{usd(out.perDollar)}</dd></div>
              <div className="border-b border-border py-3"><dt className="text-muted-foreground">Marketing ROI</dt><dd className="text-base font-semibold">{one(out.roi)}%</dd></div>
            </dl>
            {leadInfo && leadInfo.ok && (
              <p className="mt-3">
                Cost per lead: <strong>{usd(leadInfo.cpl)}</strong>. Lead-to-case rate: <strong>{one(leadInfo.rate)}%</strong>.
              </p>
            )}
            {leadInfo && !leadInfo.ok && (
              <p className="mt-3 text-muted-foreground">Leads should be at least as many as signed cases. Check the numbers.</p>
            )}
            <p className="mt-3 text-muted-foreground">
              Contingency fees can arrive months after the case is signed, so compare periods carefully.
            </p>
            <p className="mt-3">
              Not sure your numbers are right?{" "}
              {ctaHref.startsWith("#") ? <a href={ctaHref} className={`font-medium underline underline-offset-4 ${focusRing}`}>{ctaText}</a> : <Link to={ctaHref} className={`font-medium underline underline-offset-4 ${focusRing}`}>{ctaText}</Link>}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
